import { Link } from 'react-router-dom';
import styled from 'styled-components';

export const HeaderWrap = styled.header`
  background-color: ${({ theme }) => theme.primaryColor.twilightBlue};
  height: 65px;
  display: flex;
  align-items: center;
  padding: 0 24px;
  gap: 32px;
  position: relative;
`;

export const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  flex-shrink: 0;
`;

export const BrandLogo = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`;

export const BrandName = styled.span`
  font-family: ${({ theme }) => theme.fontFamily.firstFont};
  font-size: ${({ theme }) => theme.fontSize.get('s')};
  font-weight: 700;
  color: ${({ theme }) => theme.primaryColor.pristineSnowfall};
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLink = styled(Link)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 6px;
  text-decoration: none;
  font-family: ${({ theme }) => theme.fontFamily.firstFont};
  font-size: ${({ theme }) => theme.fontSize.get('s')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease;

  color: ${({ $active, theme }) => ($active ? theme.primaryColor.pristineSnowfall : theme.primaryColor.gentleAzure)};

  background-color: ${({ $active, theme }) => ($active ? 'rgba(255, 255, 255, 0.12)' : 'transparent')};

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: ${({ theme }) => theme.primaryColor.pristineSnowfall};
  }
`;

export const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

export const UserAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.primaryColor.royalBlue};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.primaryColor.pristineSnowfall};
  font-family: ${({ theme }) => theme.fontFamily.firstFont};
  font-size: ${({ theme }) => theme.fontSize.get('xs')};
  font-weight: 700;
  text-transform: uppercase;
  flex-shrink: 0;
`;

export const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    display: none;
  }
`;

export const UserName = styled.span`
  font-family: ${({ theme }) => theme.fontFamily.firstFont};
  font-size: ${({ theme }) => theme.fontSize.get('xs')};
  font-weight: 600;
  color: ${({ theme }) => theme.primaryColor.pristineSnowfall};
  line-height: 1.2;
`;

export const UserRole = styled.span`
  font-family: ${({ theme }) => theme.fontFamily.firstFont};
  font-size: ${({ theme }) => theme.fontSize.get('2xs')};
  font-weight: 400;
  color: ${({ theme }) => theme.primaryColor.gentleAzure};
  line-height: 1.2;
  text-transform: capitalize;
`;

export const ChevronIcon = styled.svg<{ $open?: boolean }>`
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.primaryColor.gentleAzure};
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  flex-shrink: 0;

  @media (max-width: 900px) {
    display: none;
  }
`;

export const DropdownMenu = styled.div<{ $top?: number; $right?: number }>`
  position: fixed;
  top: ${({ $top }) => ($top !== undefined ? `${$top}px` : '69px')};
  right: ${({ $right }) => ($right !== undefined ? `${$right}px` : '24px')};
  background-color: ${({ theme }) => theme.primaryColor.pristineSnowfall};
  border: 1px solid ${({ theme }) => theme.primaryColor.lunarElegance};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  z-index: 9999;
  min-width: 180px;
`;

export const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily.firstFont};
  font-size: ${({ theme }) => theme.fontSize.get('xs')};
  font-weight: 500;
  color: ${({ theme }) => theme.primaryColor.duskHarbor};
  text-align: left;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.primaryColor.snow};
  }
`;

export const DropdownDivider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.primaryColor.lunarElegance};
  margin: 4px 0;
`;

export const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.primaryColor.gentleAzure};
  padding: 6px;
  border-radius: 6px;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: ${({ theme }) => theme.primaryColor.pristineSnowfall};
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

export const MobileNavOverlay = styled.div<{ $open?: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    position: fixed;
    top: 65px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 40;
  }
`;

export const MobileNavPanel = styled.div`
  background-color: ${({ theme }) => theme.primaryColor.twilightBlue};
  width: 260px;
  height: 100%;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
`;

export const MobileNavLink = styled(Link)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 6px;
  text-decoration: none;
  font-family: ${({ theme }) => theme.fontFamily.firstFont};
  font-size: ${({ theme }) => theme.fontSize.get('s')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active, theme }) => ($active ? theme.primaryColor.pristineSnowfall : theme.primaryColor.gentleAzure)};
  background-color: ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.12)' : 'transparent')};

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: ${({ theme }) => theme.primaryColor.pristineSnowfall};
  }
`;

export const OrgSwitchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;

export const OrgItem = styled.div`
  cursor: pointer;
  display: flex;
  padding: 10px 20px;
  border-radius: 20px;
  align-items: center;
  text-transform: capitalize;
  font-weight: 500;
  color: ${({ theme }) => theme.primaryColor.duskHarbor};
  background: ${({ theme }) => theme.primaryColor.lunarElegance};
  font-family: ${({ theme }) => theme.fontFamily.firstFont};
  font-size: ${({ theme }) => theme.fontSize.get('xs')};
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.primaryColor.denseGray};
  }
`;
